import React, { useState } from 'react';
import {
  Modal,
  Backdrop,
  Box,
  Stack,
  Typography,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  Button,
  FormControl,
  InputLabel,
  useTheme,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { countries } from '../utils/helper';
import EmptyCheckBox from '../svg/EmptyCheckBox';
import CheckedBox from '../svg/CheckedBox';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (countryCode: string, phoneNumber: string) => void;
}

export default function WhatsAppConsentModal({ open, onClose, onSave }: Props) {
  const theme = useTheme();
  const [country, setCountry] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [accepted, setAccepted] = useState(true);

  const isSaveDisabled = !country || !phoneNumber || !accepted;

  const handleSave = () => {
    if (isSaveDisabled) return;
    const code = country.match(/\(([^)]+)\)/)?.[1] || '';
    onSave(code, phoneNumber);
    handleClose();
  };

  const handleClose = () => {
    setCountry('');
    setPhoneNumber('');
    setAccepted(true);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.5)' } } }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 420 },
          bgcolor: theme.palette.background.paper,
          borderRadius: 4,
          boxShadow: '0px 10px 30px rgba(0,0,0,0.15)',
          p: 4,
          outline: 'none',
        }}
      >
        <Stack spacing={4} alignItems='center'>
          <Stack alignItems='center' spacing={1}>
            <WhatsAppIcon sx={{ fontSize: 48, color: '#25D366' }} />
            <Typography variant='h6' fontWeight={600} color='text.primary'>
              Continue on WhatsApp
            </Typography>
            <Typography variant='body2' color='text.secondary' align='center'>
              This number will be used to chat on WhatsApp.
            </Typography>
          </Stack>

          <Stack spacing={3} width='100%'>
            <FormControl fullWidth variant='outlined'>
              <InputLabel
                id='country-select-label'
                shrink={true}
                sx={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  '&.Mui-focused': {
                    color: 'text.secondary',
                  },
                }}
              >
                Countries
              </InputLabel>
              <Select
                labelId='country-select-label'
                value={country}
                onChange={(e) => setCountry(e.target.value as string)}
                label='Countries'
                notched={true}
                displayEmpty
                MenuProps={{ PaperProps: { sx: { mt: 0.5 } } }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.secondary,
                  },
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <span style={{ color: theme.palette.text.disabled }}>
                        Select Country
                      </span>
                    );
                  }
                  return selected;
                }}
              >
                {countries.map((c) => (
                  <MenuItem key={c.code} value={`${c.name} (${c.code})`}>
                    {c.name} ({c.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label='WhatsApp Number'
              placeholder='ex: 636-555-3472'
              value={phoneNumber}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[\d\s\-+]*$/.test(value)) {
                  setPhoneNumber(value);
                }
              }}
              variant='outlined'
              InputLabelProps={{
                shrink: true,
                sx: {
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  '&.Mui-focused': {
                    color: 'text.secondary',
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: theme.palette.divider },
                  '&:hover fieldset': {
                    borderColor: theme.palette.text.secondary,
                  },
                },
              }}
            />
          </Stack>

          <Stack direction='row' alignItems='flex-start' spacing={1}>
            <Checkbox
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              icon={<EmptyCheckBox />}
              checkedIcon={<CheckedBox />}
              sx={{
                padding: 0,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            />
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              By accepting, you allow Soopra to save your number for future
              conversations.
            </Typography>
          </Stack>

          <Stack direction='row' spacing={2} width='100%'>
            <Button
              fullWidth
              onClick={handleClose}
              variant='contained'
              disableElevation
              sx={{
                bgcolor: '#DFE0FF !important',
                color: 'black !important',
                fontSize: 15,
                fontWeight: 500,
                fontFamily: 'Inter',
                textTransform: 'none',
                letterSpacing: '0.46px',
                py: 1,
                px: 2.75,
                borderRadius: 2,
                boxShadow: '0px 2px 12px rgba(0,0,0,0.08)',
                '&:hover': {
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.grey[700]
                      : '#C8C9FF',
                },
              }}
            >
              Cancel
            </Button>

            <Button
              fullWidth
              disabled={isSaveDisabled}
              onClick={handleSave}
              variant='contained'
              disableElevation
              sx={{
                bgcolor: isSaveDisabled
                  ? theme.palette.action.disabledBackground
                  : '#2542FB',
                color: isSaveDisabled ? theme.palette.text.disabled : 'white',
                fontSize: 15,
                fontWeight: 500,
                fontFamily: 'Inter',
                textTransform: 'none',
                letterSpacing: '0.46px',
                py: 1,
                px: 2.75,
                borderRadius: 2,
                boxShadow: '0px 2px 12px rgba(0,0,0,0.08)',
                '&:hover': {
                  bgcolor: isSaveDisabled
                    ? theme.palette.action.disabledBackground
                    : '#1a33d4',
                  color: isSaveDisabled ? theme.palette.text.disabled : 'white',
                },
                '&.Mui-disabled': {
                  bgcolor: theme.palette.action.disabledBackground,
                  color: theme.palette.text.disabled,
                },
              }}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
}
